import React, { useEffect, useReducer } from 'react';
import { WrappedComponentProps, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import Gallery from '../../common/gallery';
import QuickLinkItemElement from './quick-link-item-element';
import { HEARING_AIDS_URL_PARAMS, IAction } from '../../../models/constants';
import { ISession } from '../../../models/pro-sites';
import { RootState } from '../../../store';
import { hasBucket } from '../../authorization/buckets';
import { hasFeature } from '../../authorization/features';

interface IQuickLinkGalleryProps {
    session: ISession;
}

const responsive = {
    0: { items: 1 },
    1024: { items: 4 },
};
const responsiveLower = {
    0: { items: 1 },
    1024: { items: 5 },
};
export const QuickLinkGallery = ({ session }: IQuickLinkGalleryProps) => {
    //function to take in if it's a bucket or if it's a feature
    //TO DO: add the things to the feature table dummie
    const intl = useIntl();
    function statusReducer(
        state: JSX.Element[],
        action: IAction
    ): JSX.Element[] {
        switch (action.type) {
            case 'Shop':
                return [
                    ...state,

                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Shop_Hearing_Aids',
                        })}
                        imageUrl="/estore/images/DIGT3372-00-EE-SG ICONS-05 Shop HA.png"
                        imageAltText={intl.formatMessage({
                            id: 'Shop_Hearing_Aids',
                        })}
                        // SmartLink will automatically route /prosite urls within the SPA
                        callToActionUrl={
                            '/prosite/estore/product-list?' +
                            HEARING_AIDS_URL_PARAMS
                        }
                    />,
                ];
            case 'Order History':
                return [
                    ...state,

                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'My_Orders',
                        })}
                        imageUrl="/estore/images/DIGT3372-00-EE-SG ICONS-06 My Orders.png"
                        imageAltText={intl.formatMessage({
                            id: 'My_Orders',
                        })}
                        // SmartLink will automatically route /prosite urls within the SPA
                        callToActionUrl="/prosite/my-account/order-history"
                    />,
                ];
            case 'Hearing Aid History':
                return [
                    ...state,

                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Device_History',
                        })}
                        imageUrl="/estore/images/DIGT3372-00-EE-SG ICONS-07 Device History.png"
                        imageAltText={intl.formatMessage({
                            id: 'Device_History',
                        })}
                        // this will go to the old site
                        callToActionUrl="/my-account/hearing-aid-history"
                    />,
                ];
            case 'Warranty Center':
                return [
                    ...state,

                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Warranty_Center',
                        })}
                        imageUrl="/estore/images/DIGT3372-00-EE-SG ICONS-08 Add Warranty.png"
                        imageAltText={intl.formatMessage({
                            id: 'Warranty_Center',
                        })}
                        callToActionUrl="/my-account/online-forms/warranty-center"
                    />,
                ];
            default:
                break;
        }
        return state;
    }
    function statusReducerLower(
        state: JSX.Element[],
        action: IAction
    ): JSX.Element[] {
        switch (action.type) {
            case 'Impressions':
                return [
                    ...state,
                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Impressions_On_File',
                            defaultMessage: 'Impressions On File',
                        })}
                        // this will go to the old site
                        callToActionUrl="/my-account/hearing-aid-history"
                        isImpressions={true}
                    />,
                ];
            case 'Repairs':
                return [
                    ...state,
                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Repairs_AND_Remakes',
                        })}
                        callToActionUrl="/my-account/online-forms/service-request/repair"
                    />,
                ];
            case 'L&D':
                return [
                    ...state,

                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Loss_AND_Damage_Claim',
                        })}
                        callToActionUrl="/my-account/online-forms/loss-damage-claim"
                    />,
                ];
            case 'Receiver Replacement':
                return [
                    ...state,
                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Receiver_Replacement',
                        })}
                        callToActionUrl="/my-account/online-forms/receiver-replacement-ordering"
                    />,
                ];
            case 'Returns':
                return [
                    ...state,
                    <QuickLinkItemElement
                        title={intl.formatMessage({
                            id: 'Returns',
                        })}
                        callToActionUrl="/my-account/online-forms/service-request/return-for-credit"
                    />,
                ];
            default:
                break;
        }
        return state;
    }

    const QuickLinksItemList: JSX.Element[] = [];
    const [state, dispatch] = useReducer(statusReducer, QuickLinksItemList);
    const QuickLinksItemListLower: JSX.Element[] = [];
    const [stateLower, dispatchLower] = useReducer(
        statusReducerLower,
        QuickLinksItemListLower
    );
    const checkLink = (type: string, bucket?: number, feature?: string) => {
        if (type === 'Bucket' && bucket) {
            return hasBucket(session, bucket);
        } else if (type === 'Feature' && feature) {
            return hasFeature(session, feature);
        }
        return false;
    };

    useEffect(() => {
        if (checkLink('Bucket', 3)) {
            dispatch({ type: 'Shop' });
        }
        if (checkLink('Feature', undefined, 'Order History')) {
            dispatch({ type: 'Order History' });
        }
        if (checkLink('Bucket', 5)) {
            dispatch({ type: 'Hearing Aid History' });
        }
        if (checkLink('Bucket', 8)) {
            dispatch({ type: 'Warranty Center' });
        }
        if (checkLink('Bucket', 5)) {
            dispatchLower({ type: 'Impressions' });
        }
        if (checkLink('Bucket', 9)) {
            dispatchLower({ type: 'L&D' });
        }
        if (checkLink('Feature', undefined, 'Repairs')) {
            dispatchLower({ type: 'Repairs' });
        }
        if (checkLink('Feature', undefined, 'Receiver Replacement')) {
            dispatchLower({ type: 'Receiver Replacement' });
        }
        if (checkLink('Feature', undefined, 'Returns')) {
            dispatchLower({ type: 'Returns' });
        }
    }, []);
    return (
        <>
            <Gallery
                title={intl.formatMessage({
                    id: 'Quick_links',
                    defaultMessage: 'Quick Links',
                })}
                titleIcon="fal fa-box-alt" // fal fa-box-alt (pro version)
                marginBottom={20}
                responsive={responsive}
                items={state}
            />

            <Gallery
                marginBottom={60}
                responsive={responsiveLower}
                items={stateLower}
            />
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    session: state.session!.data!,
});

const mapDispatchToProps = {};

const quickLinkGalleryContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(QuickLinkGallery);

export default quickLinkGalleryContainer;
